<template>
    <transition name="pop-in" appear @after-enter="onAfterEnter">
        <div
            class="h-full w-full widget-wrapper relative"
            :style="{ 'transition-delay': `${transitionDelay}s` }"
            @mouseenter="hovered = true"
            @mouseleave="hovered = false"
        >
            <transition name="control-panel">
                <div v-if="isEditable && hovered && !beingDragged" class="widget-control-panel">
                    <vx-action-icon
                        key="delete"
                        is-button
                        icon="TrashIcon"
                        color="danger"
                        tooltip-position="top"
                        tooltip="Delete"
                        @click="showDeleteActionPrompt"
                    />
                    <vx-action-icon
                        key="duplicate"
                        is-button
                        icon="CopyIcon"
                        class="no-drag"
                        tooltip-position="top"
                        tooltip="Drag to Duplicate"
                        draggable
                        @dragstart.native="duplicateDragStart($event)"
                        @dragend.native="dragEnd($event)"
                    />
                    <vx-action-icon
                        v-if="hasConfigSlot"
                        key="settings"
                        is-button
                        icon="SettingsIcon"
                        color="warning"
                        tooltip-position="top"
                        tooltip="Settings"
                        @click="showConfig"
                    />
                </div>
            </transition>

            <div
                v-if="isEditable"
                class="vue-draggable-handle flex justify-center items-center"
                draggable
                @mousedown="beingDragged = true"
                @mouseup="beingDragged = false"
            >
                <vx-basic-icon icon="MoveIcon" size="10"></vx-basic-icon>
            </div>
            <div
                v-if="cardless"
                class="widget"
                :ref="`widget_${id}`"
                :style="styles"
                :class="{ 'overflow-visible': overflowEnabled }"
            >
                <div
                    class="no-drag relative"
                    :class="{ 'pointer-events-none': isEditable }"
                    :style="scaleStyle"
                >
                    <slot v-if="showPreview" name="preview"></slot>
                    <slot v-else :config="currentConfig"></slot>
                </div>
            </div>
            <vx-card
                v-else
                class="widget h-full"
                :ref="`widget_${id}`"
                :style="styles"
                :class="{ 'overflow-visible': overflowEnabled }"
            >
                <template v-slot:no-body>
                    <div
                        class="no-drag p-4 relative"
                        :class="{ 'pointer-events-none': isEditable }"
                        :style="scaleStyle"
                    >
                        <slot v-if="showPreview" name="preview"></slot>
                        <slot v-else :config="currentConfig"></slot>
                    </div>
                </template>
            </vx-card>
            <vx-prompt
                :active.sync="showDeletePrompt"
                title="Widget Action"
                accept-text="Delete"
                accept-color="danger"
                @accept="remove"
                @cancel="showDeletePrompt = false"
                @close="showDeletePrompt = false"
            >
                Are you sure you want to delete this widget?
            </vx-prompt>

            <vx-prompt
                title="Settings"
                :active.sync="configOpen"
                @accept="saveCurrentConfig"
                @cancel="closeConfig"
                @close="closeConfig"
            >
                <slot name="config" :config="currentConfig"></slot>
            </vx-prompt>
        </div>
    </transition>
</template>
<script>
import { GridItem } from 'vue-grid-layout'
import { mapGetters } from 'vuex'
import _color from '@/assets/utils/color.js'

export default {
    name: 'vx-widget',
    components: {
        GridItem
    },
    props: {
        color: {
            type: String
        },
        widget: {
            type: Object,
            default: () => {}
        },
        isEditable: {
            type: Boolean
        },
        title: {
            type: String
        },
        subtitle: {
            type: String
        },
        category: {
            type: String
        },
        h: {
            // dragover/default size
            type: [String, Number],
            default: 5
        },
        w: {
            // dragover/default size
            type: [String, Number],
            default: 10
        },
        scale: {
            type: Number,
            default: 1
        },
        isResizable: {
            type: Boolean,
            default: false
        },
        allowUserScaling: {
            type: Boolean,
            default: false
        },
        defaultConfig: {
            type: Object,
            default: () => {}
        },
        overflowEnabled: {
            type: Boolean
        },
        cardless: {
            type: Boolean
        }
    },
    watch: {
        originalConfig: {
            handler(v) {
                if (!v) {
                    this.currentConfig = {}
                    return
                }
                this.currentConfig = this.deepCopy(v)
            },
            immediate: true,
            deep: true
        }
    },
    data() {
        return {
            configOpen: false,
            showPreview: false,
            showDeletePrompt: false,
            currentConfig: {},
            hovered: false,
            beingDragged: false,
            animationEnded: false
        }
    },
    computed: {
        transitionDelay() {
            if (this.animationEnded) {
                return 0
            }
            return (Math.random() * 0.6).round(2)
        },
        scaleStyle() {
            return {
                'transform': `scale(${this.scale_x})`,
                'transform-origin': 'top left',
                'width': `${100 / this.scale}%`,
                'height': `${100 / this.scale}%`
            }
        },
        scale_x() {
            if (!this.widget || !this.allowUserScaling) {
                return this.scale
            }
            var userScaling = this.widget.w / Number(this.w)
            return userScaling * this.scale
        },
        id() {
            return this.widget && this.widget.id
        },
        originalConfig() {
            return this.widget && this.widget.config
        },
        hasConfigSlot() {
            return !!this.$slots.config || !!this.$scopedSlots.config
        },
        color_x() {
            if (!this.color) {
                return ''
            }
            return _color.getColorString(this.color)
        },
        styles() {
            var external = this.$attrs.style || []
            return {
                ...external,
                'background-color': this.color_x
            }
        }
    },
    methods: {
        async saveCurrentConfig() {
            var valid = await this.validate()
            if (!valid) {
                return
            }
            this.$emit('config-updated', this.currentConfig)
            this.closeConfig()
        },
        onAfterEnter() {
            this.animationEnded = true
        },
        refreshCard() {
            this.$emit('refreshCard')
        },
        showConfig() {
            this.configOpen = true
        },
        closeConfig() {
            this.configOpen = false
        },
        remove() {
            this.$emit('remove', this.widget)
        },
        showDeleteActionPrompt() {
            this.showDeletePrompt = true
        },
        duplicateDragStart(e) {
            e.dataTransfer.setData('widget', null)
            this.$store.commit('dashboard/SET_CURRENT_DRAGGED_WIDGET', {
                w: Number(this.widget.w),
                h: Number(this.widget.h),
                isResizable: this.isResizable,
                defaultConfig: this.widget.config,
                component: this.widget.component
            })
            this.$emit('dragstart', e)
        },
        dragEnd(e) {
            this.$emit('dragend', e)
        }
    }
}
</script>

<style lang="scss" scoped>
.widget-control-panel {
    gap: 0.5rem;
    position: absolute;
    display: flex;
    flex-direction: row;
    right: -0.5rem;
    top: -0.5rem;
    z-index: 2;
    padding: 0.5rem;
    background-color: rgba(var(--vs-light), 0.7);
    border-radius: 6px;
    border: 1px solid rgba(var(--vs-dark), 0.15);
}

.widget {
    height: 100%;
    overflow: hidden;
}

.vue-draggable-handle {
    transition: all 0.3s ease;
    width: 100%;
    height: 100%;
    position: absolute;
    border-radius: 0.5rem;
    z-index: 1;
    cursor: grab;

    .vx-basic-icon {
        transition: all 0.2s ease;
        opacity: 0;
        transform: scale(0.1);
    }

    &:hover {
        background-color: rgba(var(--vs-primary), 0.3);

        .vx-basic-icon {
            opacity: 0.8;
            transform: scale(1);
        }
    }

    &:active {
        cursor: grabbing;
    }
}

.control-panel-enter {
    opacity: 0;
    transform: translateX(30%);
}

.control-panel-enter-active {
    transition: opacity 0.25s, transform 0.3s;
}
</style>
